import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { exportView } from "../../navigation";
import { CustomizableForm } from "../../common/components/customizable-form";
import { useApi } from "../../common/hooks";
import { CustomizableProjectPagesService, MyService } from "../../api";
import { CustomButton, CustomDialog, ViewBanner } from "../../common/components";
import { isNonEmptyArray } from "../../common/utils";
import styles from "./ProjectSynthesis.module.css";
import { hasPermission, Permissions, translate } from "../../common/providers";
import { PROJECT_STATUS } from "../../common/constants";

const ProjectSynthesis = () => {
	const [selectedPage, setSelectedPage] = useState("");
	const [checkedPages, setCheckedPages] = useState(false);
	const [selectedLayout, setSelectedLayout] = useState(null);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const portfolioInfo = useSelector(({ context }) => context?.portfolioInfo);
	const [ongoingArchiveSetting, setOngoingArchiveSetting] = useState(false);
	const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
	const { call: getAllCustomPages } = useApi(CustomizableProjectPagesService.getAllCustomPages);
	const { call: getCustomPage } = useApi(CustomizableProjectPagesService.getCustomPage);
	const { call: getProjectDetails } = useApi(MyService.getProjectDetails);
	const { call: setProjectArchiveStatus } = useApi(CustomizableProjectPagesService.setProjectArchiveStatus);
	const [isActive, setIsActive] = useState(false);
	const portfolioName = useMemo(() => portfolioInfo?.name || "Portfolio", [portfolioInfo]);
	const hasEditorRights = useMemo(
		() =>
			hasPermission([Permissions.COMPANY_ADMIN, Permissions.COMPANY_MANAGER]) &&
			hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]),
		[]
	);
	const hasGestionnaireRights = useMemo(
		() => hasPermission([Permissions.COMPANY_ADMIN]) && hasPermission([Permissions.PROJECT_LEADER]),
		[]
	);

	useEffect(() => {
		getProjectDetails({ projectId })
			.then((data) => {
				setIsActive(data?.status === PROJECT_STATUS.ACTIVE);
			})
			.catch(console.error);
	}, [getProjectDetails, projectId]);

	useEffect(() => {
		if (!checkedPages) {
			getAllCustomPages({ projectId })
				.then((data) => {
					if (isNonEmptyArray(data)) {
						setSelectedPage(data[0]);
					}
				})
				.catch(console.error)
				.finally(() => {
					setCheckedPages(true);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkedPages]);
	const adjustlayoutPayload = (newSections) => {
		setSelectedLayout(JSON.parse(newSections));
	};
	useEffect(() => {
		if (selectedPage) {
			const { id: customPageId } = selectedPage;
			getCustomPage({ projectId, customPageId })
				.then((data) => {
					adjustlayoutPayload(data.value);
				})
				.catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPage]);
	const handleArchiveStatus = () => {
		if (hasEditorRights) {
			setOngoingArchiveSetting(true);
			setProjectArchiveStatus({ projectId }, (isActive && PROJECT_STATUS.ARCHIVED) || PROJECT_STATUS.ACTIVE)
				.then(() => {
					window.location.reload();
				})
				.catch(console.error)
				.finally(() => setOngoingArchiveSetting(false));
		}
	};
	const handleOpenArchiveDialog = () => {
		setOpenArchiveDialog((prev) => !prev);
	};
	return (
		<div className={styles.main}>
			<ViewBanner titles={[{ title: portfolioName.slice(0, -1) }]} />
			<div className={styles.header}>
				<span className={styles.headerTitle}>{portfolioName.slice(0, -1)}</span>
				{hasGestionnaireRights && (
					<CustomButton
						color="secondary"
						disabled={ongoingArchiveSetting}
						variant="outlined"
						onClick={handleOpenArchiveDialog}
					>
						{translate(
							(isActive && `common:project.status.archive.${portfolioName.toLowerCase()}`) ||
								`common:project.status.unarchive.${portfolioName.toLowerCase()}`
						)}
					</CustomButton>
				)}
			</div>
			<div className={styles.content}>
				{selectedLayout && (
					<div className={styles.container}>
						<CustomizableForm
							customPageId={selectedPage?.id}
							editMode={false}
							hasEditorRights={hasEditorRights && isActive}
							projectId={projectId}
							sections={selectedLayout}
						/>
					</div>
				)}
			</div>
			<CustomDialog
				closeLabel={translate("common:btn.no")}
				open={openArchiveDialog}
				submitLabel={translate("common:btn.yes")}
				subTitle={translate(
					(isActive && `common:project.archive.dialog.confirmation.archive.${portfolioName.toLowerCase()}`) ||
						`common:project.archive.dialog.confirmation.unarchive.${portfolioName.toLowerCase()}`
				)}
				onClose={handleOpenArchiveDialog}
				onSubmit={handleArchiveStatus}
			/>
		</div>
	);
};

export default exportView({
	path: [{ path: "/projects/:projectId/project-synthesis" }],
	localesPath: "/my-review/locales",
	component: ProjectSynthesis,
});
